import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions {
	/** Fraction of the element that must be visible before firing (0–1). */
	threshold?: number;
	/** Root margin passed to the observer (e.g. to trigger slightly early). */
	rootMargin?: string;
}

/**
 * IntersectionObserver-based "has this element scrolled into view yet" hook,
 * with `once` semantics: the first time the element intersects, `inView`
 * latches to `true` and the observer disconnects — it never re-fires when the
 * element scrolls back out and in again.
 *
 * Progressive-enhancement contract: `inView` is only ever used to *enhance*
 * already-rendered content (e.g. a Recharts grow-in). It MUST NOT gate the
 * content's existence. If the observer never fires — no JS, a test env without
 * a real viewport, or reduced-motion (below) — `inView` simply stays `false`
 * and the caller renders its final, correct state with no animation.
 *
 * Under `prefers-reduced-motion: reduce` the observer is never attached and
 * `inView` stays `false`, so motion-triggered enhancements are skipped
 * entirely (the content still renders in full).
 */
export function useInView<T extends Element = HTMLDivElement>(
	options: UseInViewOptions = {},
) {
	const { threshold = 0.2, rootMargin = '0px' } = options;
	const ref = useRef<T | null>(null);
	const [inView, setInView] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (typeof IntersectionObserver === 'undefined') return;

		const prefersReducedMotion =
			typeof window !== 'undefined' &&
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (prefersReducedMotion) return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setInView(true);
						observer.disconnect();
						break;
					}
				}
			},
			{ threshold, rootMargin },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [threshold, rootMargin]);

	return { ref, inView };
}
